import React, { FC, useRef } from 'react';
import classnames from 'classnames';
import { useIntl } from 'react-intl';

import { useMute } from '@/scenarios/legacy-player/hooks/useMute';
import { useVolume } from '@/scenarios/legacy-player/hooks/useVolume';
import { MAX_VOLUME, MIN_VOLUME } from '@/scenarios/legacy-player/constants/volume';

import { useBoundingClientRect } from '../../../../../hooks/skin/use-bounding-client-rect';
import { useVolumeDrag } from '../../hooks/use-volume-drag';
import { useTurnOffGlobalKeyboardHandlers } from '../../../../../hooks/skin/use-turn-off-global-keyboard-handlers';
import { useTurnOnGlobalKeyboardHandlers } from '../../../../../hooks/skin/use-turn-on-global-keyboard-handlers';
import { KeyboardKeys } from '../../../../../constants/keyboard';
import { getAriaVoumeCurrentValue } from '../../helpers/get-aria-volume-current-value';

import styles from './styles.module.css';
import { useSliderKeyboard } from './hooks/use-slider-keyboard';

interface Props {
    className?: string;
    onFocus?: () => void;
    onBlur?: () => void;
}

const OVERRIDED_GLOBAL_PLAYER_KEYBOARD_HANDLER_KEYS = [
    KeyboardKeys.ArrowLeft,
    KeyboardKeys.ArrowRight,
    KeyboardKeys.ArrowUp,
    KeyboardKeys.ArrowDown,
    KeyboardKeys.Home,
    KeyboardKeys.End,
    KeyboardKeys.PageUp,
    KeyboardKeys.PageDown,
];

export const Slider: FC<Props> = ({ className, onFocus, onBlur }) => {
    const { isMuted } = useMute();
    const sliderRef = useRef<HTMLDivElement>(null);
    const { volume, setVolume } = useVolume();

    useSliderKeyboard(sliderRef.current);

    const { boundingClientRectState, recalculateBoundingClientRect } =
        useBoundingClientRect(sliderRef);
    const turnOffGlobalKeyboardHandler = useTurnOffGlobalKeyboardHandlers();
    const turnOnGlobalKeyboardHandler = useTurnOnGlobalKeyboardHandlers();
    const handleFocus = () => {
        onFocus?.();
        turnOffGlobalKeyboardHandler(OVERRIDED_GLOBAL_PLAYER_KEYBOARD_HANDLER_KEYS);
    };

    const handleBlur = () => {
        onBlur?.();
        turnOnGlobalKeyboardHandler(OVERRIDED_GLOBAL_PLAYER_KEYBOARD_HANDLER_KEYS);
    };

    const { isDragged, onDragStart } = useVolumeDrag({
        sliderHeight: boundingClientRectState?.height,
        containerBottomPadding: boundingClientRectState?.bottom,
    });
    const intl = useIntl();

    if (volume === undefined) {
        return null;
    }

    return (
        // we use keyboard navigation through useKeyboard hook
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events
        <div
            role="slider"
            aria-label={intl.formatMessage({
                id: 'features.player.bd7a9717d29c5ddcab1bc175eda1e298',
                defaultMessage: 'Volume',
                description: 'ARIA описание слайдера громкости',
            })}
            aria-valuenow={isMuted ? 0 : volume}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuetext={getAriaVoumeCurrentValue(intl, volume, isMuted)}
            onMouseEnter={recalculateBoundingClientRect}
            ref={sliderRef}
            className={classnames(styles.root, className)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onClick={(event) =>
                !isDragged &&
                boundingClientRectState &&
                setVolume(
                    MAX_VOLUME *
                        ((boundingClientRectState.bottom - event.clientY) /
                            boundingClientRectState.height)
                )
            }
            tabIndex={0}
        >
            <div className={styles.backLayer} />
            <div
                className={styles.progress}
                style={{
                    height: isMuted ? MIN_VOLUME : `${volume}%`,
                    maxHeight: isDragged ? boundingClientRectState?.height : undefined,
                }}
            >
                {/* we use parent slider role */}
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                <div className={styles.pin} onMouseDown={onDragStart} />
            </div>
        </div>
    );
};
